/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Java SDK", description = "Hightouch's Java SDK for collecting events", lastUpdatedDate = new Date(1710201600000);
import {Tabs, TabList, TabPanels, Tab, TabPanel} from '@chakra-ui/react';
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Java SDK",
        description: "Hightouch's Java SDK for collecting events",
        lastUpdatedDate: "Tue Mar 12 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span",
      h3: "h3",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Java SDK makes it easy to track events from Java applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Java SDK is available via\n", _jsx(_components.a, {
          href: "https://jitpack.io/#ht-sdks/events-sdk-java/",
          children: "JitPack"
        }), "."]
      }), "\n", "\n", _jsxs(Tabs, {
        children: [_jsxs(TabList, {
          children: [_jsx(Tab, {
            children: "Maven"
          }), "\n", _jsx(Tab, {
            children: "Gradle"
          })]
        }), _jsxs(TabPanels, {
          children: [_jsx(TabPanel, {
            children: _jsx(_components.pre, {
              children: _jsx(_components.code, {
                className: "hljs language-php-template",
                children: _jsxs(_components.span, {
                  className: "xml",
                  children: [_jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "repositories"
                    }), ">"]
                  }), "\n  ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "repository"
                    }), ">"]
                  }), "\n    ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "id"
                    }), ">"]
                  }), "jitpack.io", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "id"
                    }), ">"]
                  }), "\n    ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "url"
                    }), ">"]
                  }), "https://jitpack.io", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "url"
                    }), ">"]
                  }), "\n  ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "repository"
                    }), ">"]
                  }), "\n", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "repositories"
                    }), ">"]
                  }), "\n\n", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "dependency"
                    }), ">"]
                  }), "\n  ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "groupId"
                    }), ">"]
                  }), "com.github.ht-sdks.events-sdk-java", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "groupId"
                    }), ">"]
                  }), "\n  ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "artifactId"
                    }), ">"]
                  }), "analytics", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "artifactId"
                    }), ">"]
                  }), "\n  ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "version"
                    }), ">"]
                  }), "LATEST", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "version"
                    }), ">"]
                  }), "\n", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "dependency"
                    }), ">"]
                  }), "\n"]
                })
              })
            })
          }), _jsx(TabPanel, {
            children: _jsx(_components.pre, {
              children: _jsxs(_components.code, {
                className: "hljs language-python",
                children: ["allprojects {\n  repositories {\n    ...\n    maven { url ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'https://jitpack.io'"
                }), " }\n  }\n}\n\ndependencies {\n  ...\n  ", _jsx(_components.span, {
                  className: "hljs-built_in",
                  children: "compile"
                }), " ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'com.github.ht-sdks.events-sdk-java:analytics:+'"
                }), "\n}\n"]
              })
            })
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To initialize the Java SDK in your application, create an ", _jsx(_components.code, {
          children: "Analytics"
        }), " instance:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-java",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " com.hightouch.analytics.Analytics;\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "public"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "class"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Main"
          }), " {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "public"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "static"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "void"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "main"
          }), _jsx(_components.span, {
            className: "hljs-params",
            children: "(String... args)"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "throws"
          }), " Exception {\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "final"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Analytics"
          }), " ", _jsx(_components.span, {
            className: "hljs-variable",
            children: "analytics"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "\n        Analytics.builder(WRITE_KEY)\n            .endpoint(API_ENDPOINT)\n            .build();\n  }\n}\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "IdentifyMessage"
        }), " sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike the ", _jsx(_components.a, {
          href: "./browser",
          children: "Browser SDK"
        }), ", it does ", _jsx(_components.strong, {
          children: "not"
        }), " persist the user ID and\ntraits locally, so user IDs must be explicitly added to other events. This is\nbecause server side events are usually servicing many different users at once."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-perl",
          children: ["analytics.enqueue(IdentifyMessage.builder()\n    .userId(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ")\n    .traits(ImmutableMap.builder()\n        .put(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"John Doe\""
          }), ")\n        .put(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"test@example.com\""
          }), ")\n        .build()\n    )\n);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "TrackMessage"
        }), " sends a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".enqueue"
          }), "(TrackMessage.builder(\"Item Purchased\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".userId"
          }), "(\"", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), "\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".properties"
          }), "(ImmutableMap.builder()\n        ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".put"
          }), "(\"revenue\", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "39.95"
          }), ")\n        ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".put"
          }), "(\"shipping\", \"", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), "-day\")\n        ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".build"
          }), "()\n    )\n);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "PageMessage"
        }), " sends a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "enqueue"
          }), "(PageMessage.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "builder"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Schedule\""
          }), ")\n    .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "userId"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ")\n    .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "properties"
          }), "(ImmutableMap.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "builder"
          }), "()\n        .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "put"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Sports\""
          }), ")\n        .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "put"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"path\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"/sports/schedule\""
          }), ")\n        .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "build"
          }), "()\n    )\n);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "ScreenMessage"
        }), " sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "enqueue"
          }), "(ScreenMessage.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "builder"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Schedule\""
          }), ")\n    .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "userId"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ")\n    .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "properties"
          }), "(ImmutableMap.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "builder"
          }), "()\n        .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "put"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Sports\""
          }), ")\n        .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "put"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"path\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"/sports/schedule\""
          }), ")\n        .", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "build"
          }), "()\n    )\n);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "GroupMessage"
        }), " sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".enqueue"
          }), "(GroupMessage.builder(\"group123\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".userId"
          }), "(\"", _jsx(_components.span, {
            className: "hljs-number",
            children: "123"
          }), "\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".traits"
          }), "(ImmutableMap.builder()\n        ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".put"
          }), "(\"name\", \"Hightouch\")\n        ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".put"
          }), "(\"size\", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "50"
          }), ")\n        ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".build"
          }), "()\n    )\n);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Map"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The Java SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush"
        }), "\nmethod. ", _jsx(_components.code, {
          children: "flush"
        }), " should be called when shutting down your Java app to\nmake sure no events are lost."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-lua",
          children: ["analytics.", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "flush"
          }), "()\n"]
        })
      })]
    });
  }
}
export default MDXContent;
